/**
 * ScrubNotificationModal
 *
 * Shown once per session when the client logs in and badLeadScrubberNightly has
 * removed leads since their last visit. Reads `pendingScrubNotification` flag
 * from the client doc. Dismissing calls `dismissScrubNotification` which
 * clears the flag.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Props {
  removedCount: number;
  scrubDate: string;
  onClose: () => void;
}

export function ScrubNotificationModal({ removedCount, scrubDate, onClose }: Props) {
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const fn = httpsCallable(functions, 'dismissScrubNotification');
      await fn({});
    } catch (err) {
      console.error('Failed to dismiss scrub notification:', err);
    } finally {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={contentStyle}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🧹</div>
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>Bad Phone Cleanup</h2>
        </div>

        <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 16px' }}>
          Since your last visit, we identified <strong style={{ color: 'var(--gold)' }}>{removedCount.toLocaleString()}</strong>{' '}
          newly disconnected {removedCount === 1 ? 'phone' : 'phones'} in your lead list and removed {removedCount === 1 ? 'it' : 'them'}.
        </p>

        <p style={{ color: 'var(--gray-300)', lineHeight: 1.6, fontSize: '0.9rem', margin: '0 0 20px' }}>
          This is part of our ongoing data quality service &mdash; we re-check your leads
          every night against the latest disconnection data so you only pay to call
          phones that are still active. The cleanup happened on{' '}
          <strong>{scrubDate}</strong>.
        </p>

        <div style={infoBoxStyle}>
          <strong style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>What this means for you</strong>
          <ul style={{ margin: '8px 0 0', padding: '0 0 0 20px', color: 'var(--gray-200)', fontSize: '0.85rem', lineHeight: 1.6 }}>
            <li>Your lead counts may show fewer pending leads than yesterday</li>
            <li>You won&rsquo;t be charged for calls to phones we removed</li>
            <li>This happens automatically every night</li>
          </ul>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="btn btn-primary"
          >
            {dismissing ? 'Dismissing...' : 'Got It'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const contentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '480px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};

const infoBoxStyle: React.CSSProperties = {
  padding: '14px 16px', borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.08)',
  border: '1px solid rgba(200, 168, 78, 0.2)',
};
